import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatRoute } from 'react-router-named-routes';
import { ActivityObjectType, EntryType } from '../../API';
import Board from '../common/board';
import { useIsMobile } from '../common/layout_hooks';
import ContentEntryDetailContainer from '../programmes/content_entry_detail_container';
import usePrevious from '../../services/use_previous';
import { scrollNewObjectCardIntoView } from '../../services/smorg_board';
import SpaceContentBoardEntryCard from './space_content_board_entry_card';
import { spaceContentBoardEntryByEntryID } from '../../services/spaces';
import SpaceContentBoardAddCardLink from './space_content_board_add_card_link';
import SpaceContentBoardNewLaneButton from './space_content_board_new_lane_button';
import SpaceContentBoardNewLaneForm from './space_content_board_new_lane_form';
import SpaceContentBoardLaneHeader from './space_content_board_lane_header';
import { SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE } from '../../services/routes';
import SharedContentBoardNavigationBar from './shared_content_board_navigation_bar';
import {
  appSyncGraphQLApi,
  useDeleteSharedContentEntryMutation,
  useGetSharedContentEntriesByParentQuery,
  useGetSpaceQuery,
  useUpdateSharedContentBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  areResourcesStaleSelector,
  StaleResourceType,
} from '../../features/stale_resource_detection/stale_resource_detection_slice';
import ResourceStatusControl from '../common/resource_status_control';
import { useGetOrCreateSpaceContentBoardsQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import StaleScreenModal from '../common/stale_screen_modal';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const components = {
  Card: SpaceContentBoardEntryCard,
  AddCardLink: SpaceContentBoardAddCardLink,
  NewLaneForm: SpaceContentBoardNewLaneForm,
  NewLaneSection: SpaceContentBoardNewLaneButton,
  LaneHeader: SpaceContentBoardLaneHeader,
};

const labels = {
  'Add another lane': '+ Add another lane',
  'Delete lane': 'Delete lane',
  'Lane actions': 'Lane actions',
  button: {
    'Add lane': 'Add lane',
    'Add card': 'Add content',
    Cancel: 'Cancel',
  },
  placeholder: {
    title: 'title',
    description: 'description',
    label: 'label',
  },
};

const customTranslation = (key) => labels[key];

const boardCss = { textAlign: 'left' };
// This screen is not meant to be used on mobile screens
const mobileHeightRule = { height: 'calc(var(--app-height) - 119px)' };
const desktopHeightRule = { height: 'calc(var(--app-height) - 84px)' };
const mobileBoardCss = { ...boardCss, ...mobileHeightRule };
const desktopBoardCss = { ...boardCss, ...desktopHeightRule };

const SpaceContentBoardView = () => {
  const dispatch = useDispatch();

  const { data: space } = useGetSpaceQuery();

  const { sharedContentBoardID: routeSharedContentBoardID } = useParams();

  const { data: sharedContentBoards } = useGetOrCreateSpaceContentBoardsQuery();

  const [updateSharedContentBoard] = useUpdateSharedContentBoardMutation();

  const [deleteSharedContentEntry] = useDeleteSharedContentEntryMutation();

  const spaceContentBoard = useMemo(
    () =>
      routeSharedContentBoardID
        ? (sharedContentBoards || []).find(
            (b) => b.id === routeSharedContentBoardID,
          )
        : (sharedContentBoards || [])[0],
    [routeSharedContentBoardID, sharedContentBoards],
  );

  const { data: sharedContentEntries } =
    useGetSharedContentEntriesByParentQuery(spaceContentBoard?.id, {
      skip: !spaceContentBoard?.id,
    });

  const allEntryIDs = useMemo(
    () =>
      (spaceContentBoard?.lanes || []).flatMap((lane) =>
        lane.entries.map((e) => e.id),
      ),
    [spaceContentBoard?.lanes],
  );
  const previousEntryIDs = usePrevious(allEntryIDs);

  // console.log({ allEntryIDs, previousEntryIDs });

  useEffect(() => {
    scrollNewObjectCardIntoView(allEntryIDs, previousEntryIDs);
  }, [allEntryIDs, previousEntryIDs]);

  const navigate = useNavigate();

  const { entryId } = useParams();

  const detailVisibleFor = useMemo(() => {
    if (!entryId || !spaceContentBoard?.lanes) {
      return null;
    }
    const entry = spaceContentBoardEntryByEntryID(spaceContentBoard, entryId);
    if (!entry) {
      return null;
    }
    return {
      entryID: entryId,
      entryType: entry.entryType,
      objectID: entry.objectID,
      parentID: spaceContentBoard.id,
    };
  }, [entryId, spaceContentBoard]);

  const reactTrelloData = {
    lanes: (spaceContentBoard?.lanes || []).map((lane) => {
      const cards = lane.entries
        .map((entry) => {
          if (entry.entryType === EntryType.CONTENT_ENTRY) {
            const sharedContentEntry = (sharedContentEntries || {})[
              entry.objectID
            ];
            if (!sharedContentEntry) {
              // Note not yet loaded
              return null;
            }
            return {
              id: entry.id,
              title: sharedContentEntry.title,
              description: sharedContentEntry.body || '',
              style: {
                _entryType: entry.entryType,
                _objectID: entry.objectID,
              },
            };
          }
          return null;
        })
        .filter((cardData) => !!cardData);

      return {
        id: lane.id,
        title: lane.title,
        cards,
      };
    }),
  };

  const onCardClick = useCallback(
    (cardId, metadata, laneId) => {
      console.log({ cardId, metadata, laneId });
      navigate(
        formatRoute(SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ENTRY_ROUTE, {
          sharedContentBoardID: spaceContentBoard.id,
          entryId: cardId,
        }),
      );
    },
    [navigate, spaceContentBoard?.id],
  );

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const afterCardDrag = useCallback(
    async (cardId, sourceLaneId, targetLaneId, position) => {
      const entry = spaceContentBoardEntryByEntryID(spaceContentBoard, cardId);
      const updatedLanes = spaceContentBoard.lanes.map((lane) => {
        let updatedLane = { ...lane };

        if (updatedLane.id === sourceLaneId) {
          updatedLane = {
            ...updatedLane,
            entries: updatedLane.entries.filter((e) => e.id !== cardId),
          };
        }

        if (updatedLane.id === targetLaneId) {
          const updatedEntries = [...updatedLane.entries];
          updatedEntries.splice(position, 0, entry);
          updatedLane = {
            ...updatedLane,
            entries: updatedEntries,
          };
        }

        return updatedLane;
      });
      const updatedBoard = {
        ...spaceContentBoard,
        lanes: updatedLanes,
      };
      await updateSharedContentBoard(updatedBoard);
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
        lastActivityObjectID: spaceContentBoard.id,
      });

      // Cancel the drop, because the board will re-render with the new structure.
      return false;
    },
    [friendlyUsername, space?.id, spaceContentBoard, updateSharedContentBoard],
  );

  const afterCardDelete = useCallback(
    async (cardId, laneId) => {
      const entry = spaceContentBoardEntryByEntryID(spaceContentBoard, cardId);
      if (!entry) {
        return;
      }
      const { objectID } = entry;
      try {
        if (entry.programmeEntryType === EntryType.CONTENT_ENTRY) {
          await deleteSharedContentEntry({
            id: objectID,
            parentID: spaceContentBoard.id,
          });
        }
      } finally {
        const updatedLanes = spaceContentBoard.lanes.map((lane) => {
          if (lane.id !== laneId) {
            return lane;
          }
          const updatedEntries = lane.entries.filter((e) => e.id !== cardId);
          return {
            ...lane,
            entries: updatedEntries,
          };
        });
        const updatedBoard = {
          ...spaceContentBoard,
          lanes: updatedLanes,
        };
        await updateSharedContentBoard(updatedBoard);
        reportAccountActivitySignalOperation({
          spaceID: space.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
          lastActivityObjectID: spaceContentBoard.id,
        });
      }
    },
    [
      deleteSharedContentEntry,
      friendlyUsername,
      space?.id,
      spaceContentBoard,
      updateSharedContentBoard,
    ],
  );

  const afterLaneAdd = useCallback(
    async ({ id, title }) => {
      if (title && title.length > 0) {
        const updatedBoard = {
          ...spaceContentBoard,
          lanes: [
            ...spaceContentBoard.lanes,
            {
              id,
              title,
              entries: [],
            },
          ],
        };
        reportAccountActivitySignalOperation({
          spaceID: space.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
          lastActivityObjectID: spaceContentBoard.id,
        });
        await updateSharedContentBoard(updatedBoard);
      }
    },
    [friendlyUsername, space?.id, spaceContentBoard, updateSharedContentBoard],
  );

  const afterLaneDelete = useCallback(
    async (laneId) => {
      const objectIDs = (
        spaceContentBoard.lanes.find((l) => l.id === laneId)?.entries || []
      ).map((e) => e.objectID);
      await Promise.all(
        objectIDs.map((objectID) =>
          deleteSharedContentEntry({
            id: objectID,
            parentID: spaceContentBoard.id,
          }),
        ),
      );
      const updatedLanes = spaceContentBoard.lanes.filter(
        (l) => l.id !== laneId,
      );
      const updatedBoard = {
        ...spaceContentBoard,
        lanes: updatedLanes,
      };
      await updateSharedContentBoard(updatedBoard);
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
        lastActivityObjectID: spaceContentBoard.id,
      });
    },
    [
      deleteSharedContentEntry,
      friendlyUsername,
      space?.id,
      spaceContentBoard,
      updateSharedContentBoard,
    ],
  );

  const afterLaneDrag = useCallback(
    async (removedIndex, addedIndex /* , payload */) => {
      // const laneId = payload.id;
      const updatedLanes = [...spaceContentBoard.lanes];
      const [lane] = updatedLanes.splice(removedIndex, 1);
      updatedLanes.splice(addedIndex, 0, lane);
      const updatedBoard = {
        ...spaceContentBoard,
        lanes: updatedLanes,
      };
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
        lastActivityObjectID: spaceContentBoard.id,
      });
      await updateSharedContentBoard(updatedBoard);
    },
    [friendlyUsername, space?.id, spaceContentBoard, updateSharedContentBoard],
  );

  const afterLaneUpdate = useCallback(
    async (laneId, data) => {
      console.log({ laneId, data });
      // Only the title can be edited
      const updatedTitle = data.title;
      if (updatedTitle && updatedTitle.length > 0) {
        const updatedLanes = spaceContentBoard.lanes.map((lane) => {
          if (lane.id === laneId) {
            return {
              ...lane,
              title: updatedTitle,
            };
          }

          return lane;
        });
        const updatedBoard = {
          ...spaceContentBoard,
          lanes: updatedLanes,
        };
        await updateSharedContentBoard(updatedBoard);
        reportAccountActivitySignalOperation({
          spaceID: space.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
          lastActivityObjectID: spaceContentBoard.id,
        });
      }
    },
    [friendlyUsername, space?.id, spaceContentBoard, updateSharedContentBoard],
  );

  const isMobile = useIsMobile();

  const isBoardViewStale = useSelector((state) =>
    areResourcesStaleSelector(state, 'SharedContentBoard', [
      spaceContentBoard?.id,
    ]),
  );

  const onReloadStaleData = () => {
    dispatch(appSyncGraphQLApi.util.invalidateTags(['SharedContentBoard']));
  };

  return (
    <>
      <SharedContentBoardNavigationBar
        sharedContentBoardID={spaceContentBoard?.id}
      />
      <div className="smorg-board-container" id="space-content-board">
        <Board
          editable
          draggable
          cardDraggable
          editLaneTitle
          canAddLanes
          appBoardId={spaceContentBoard?.id}
          data={reactTrelloData}
          components={components}
          onCardClick={onCardClick}
          onCardDelete={afterCardDelete}
          handleDragEnd={afterCardDrag}
          onLaneAdd={afterLaneAdd}
          onLaneDelete={afterLaneDelete}
          onLaneUpdate={afterLaneUpdate}
          handleLaneDragEnd={afterLaneDrag}
          style={isMobile ? mobileBoardCss : desktopBoardCss}
          backdropStyle={isMobile ? mobileHeightRule : desktopHeightRule}
          t={customTranslation}
        />
      </div>
      {!isBoardViewStale &&
        detailVisibleFor?.entryType === EntryType.CONTENT_ENTRY && (
          <ContentEntryDetailContainer
            contentEntryID={detailVisibleFor.objectID}
            parentSmorgBoardId={detailVisibleFor.parentID}
            visible
            appLinkEnabled
            dismiss={() => navigate(-1)}
          />
        )}
      {isBoardViewStale && <StaleScreenModal onSync={onReloadStaleData} />}
      <ResourceStatusControl
        resourceType={StaleResourceType.SharedContentBoard}
        resourceIDs={[spaceContentBoard?.id]}
      />
    </>
  );
};

export default SpaceContentBoardView;
